/**
 * Lobby Controller for Poker Reborn
 *
 * Manages lobby actions: loading games, filtering, table selection.
 * Connects to poker_platform backend via manager1 service.
 */

import { networkService } from '@/services/NetworkService';
import {
  lobbyState,
  setManagers,
  setSelectedManager,
  setTables,
  setSizeFilter,
  setStakesFilter,
  setLoading,
} from '@/state/LobbyState';
import { EventEmitter } from '@/services/EventEmitter';
import { log } from '@/utils/log';
import type { GameManager, TableInfo, TableSize, StakesLevel } from '@/types/game';
import { Commands, ServiceGLIDs, ManagerBySize } from '@/types/protocol';
import type { HotTableMessage, StateMessage } from '@/types/protocol';
import type { GameType, ManagerConfig } from '@/config/managers';
import { autoSelectManager } from '@/utils/managerSelection';
import { accountService } from '@/services/AccountService';

// =============================================================================
// TYPES
// =============================================================================

export interface LobbyControllerEvents {
  /** Games loaded */
  gamesLoaded: { managers: GameManager[] };

  /** Tables loaded */
  tablesLoaded: { tables: TableInfo[] };

  /** Table selected */
  tableSelected: { table: TableInfo };

  /** Error occurred */
  error: { message: string };
}

// =============================================================================
// LOBBY CONTROLLER
// =============================================================================

class LobbyController extends EventEmitter<LobbyControllerEvents> {
  private static instance: LobbyController | null = null;
  private pendingTableRequests: Map<string, (table: TableInfo | null) => void> = new Map();
  private discoveredCroupiers: Set<string> = new Set();

  private constructor() {
    super();
    this.setupNetworkHandlers();
  }

  /** Get singleton instance */
  static getInstance(): LobbyController {
    if (!LobbyController.instance) {
      LobbyController.instance = new LobbyController();
    }
    return LobbyController.instance;
  }

  /**
   * Load available game managers
   */
  async loadGames(): Promise<void> {
    setLoading(true);

    try {
      // Create a default manager for cash games
      const cashManager: GameManager = {
        id: 'cash_holdem',
        name: 'Cash Games',
        type: 'cash',
        description: 'No-Limit Texas Hold\'em',
        icon: 'chips',
        playerCount: 0,
        tableCount: 0,
        tables: [],
      };

      setManagers([cashManager]);
      setSelectedManager(cashManager);
      this.emit('gamesLoaded', { managers: [cashManager] });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load games';
      this.emit('error', { message });
    } finally {
      setLoading(false);
    }
  }

  /**
   * Select a game manager (e.g., Cash Games, Sit & Go)
   */
  selectManager(managerId: string): void {
    const managers = lobbyState.managers.get();
    const manager = managers.find((m) => m.id === managerId);

    if (manager) {
      setSelectedManager(manager);
      this.loadTablesForManager(manager);
    }
  }

  /**
   * Load tables for a specific manager
   */
  async loadTablesForManager(manager: GameManager): Promise<void> {
    setLoading(true);

    try {
      // TODO: Request real tables from server
      // For now, use the tables from the manager directly (mock data)
      setTables(manager.tables);
      this.emit('tablesLoaded', { tables: manager.tables });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tables';
      this.emit('error', { message });
    } finally {
      setLoading(false);
    }
  }

  /**
   * Filter tables by size
   */
  filterBySize(size: TableSize | 'all'): void {
    setSizeFilter(size);
  }

  /**
   * Filter tables by stakes level
   */
  filterByStakes(level: StakesLevel | 'all'): void {
    setStakesFilter(level);
  }

  /**
   * Get filtered tables
   */
  getFilteredTables(): TableInfo[] {
    return lobbyState.filteredTables.get();
  }

  /**
   * Select a table to watch/join
   */
  selectTable(tableId: string): TableInfo | null {
    const tables = lobbyState.tables.get();
    const table = tables.find((t) => t.id === tableId);

    if (table) {
      this.emit('tableSelected', { table });
      return table;
    }

    return null;
  }

  /**
   * Request to watch a table
   * Delegates to TableController to handle the actual watch/join
   */
  async watchTable(tableId: string): Promise<void> {
    const table = this.selectTable(tableId);
    if (!table) {
      this.emit('error', { message: 'Table not found' });
      return;
    }

    // Delegate to TableController to handle watching/joining the table
    const { tableController } = await import('@/controllers/TableController');
    await tableController.watchTable(table);
  }

  /**
   * Find a table of the specified size without joining
   * Uses # wannajumpin but returns table info for buy-in selection
   * @param tableSize - Table size (2, 6, or 9)
   */
  async findTable(tableSize: TableSize): Promise<TableInfo | null> {
    const managerGlid = ManagerBySize[tableSize] ?? ServiceGLIDs.MANAGER;
    const sizeLabel = `${tableSize}-max`;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.emit('error', { message: `No ${sizeLabel} tables available` });
        resolve(null);
      }, 5000);

      const handleMessage = ({ from, body }: { from: string; body: string }) => {
        try {
          const message = JSON.parse(body) as Record<string, unknown>;

          if (message.a === 'handoff' && message.manager === managerGlid) {
            clearTimeout(timeout);
            networkService.off('message', handleMessage);

            if (message.status === 'no_place' || !message.to) {
              log.lobby.warning`Find table: no tables available from ${managerGlid}`;
              this.emit('error', { message: `No ${sizeLabel} tables available` });
              resolve(null);
              return;
            }

            const croupierGlid = message.to as string;
            const table: TableInfo = {
              id: croupierGlid,
              name: this.formatTableName(croupierGlid),
              seats: tableSize,
              stakes: { smallBlind: 1, bigBlind: 2 }, // Default, will be updated
              playerCount: 0,
              avgPot: 0,
              handsPerHour: 0,
            };

            resolve(table);
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      networkService.on('message', handleMessage);
      networkService.send(managerGlid, Commands.wannaJumpIn());
    });
  }

  /**
   * Join a table with a specific buy-in amount
   * @param table - Table info
   * @param buyInAmount - Amount to buy in with
   * @param autoRefill - Auto refill when chips run low
   * @param autoRebuy - Auto rebuy when busted
   */
  async joinTableWithBuyIn(
    table: TableInfo,
    buyInAmount: number,
    autoRefill = false,
    autoRebuy = false
  ): Promise<void> {
    const { tableController } = await import('@/controllers/TableController');
    // Watch the table first, then the table screen will handle the buy-in
    await tableController.watchTable(table, { buyInAmount, autoRefill, autoRebuy });
  }

  /**
   * Find a table to watch (no seat reservation)
   * Uses # gethottable to just get table address without reserving
   * @param tableSize - Table size (2, 6, or 9)
   */
  async findTableToWatch(tableSize: TableSize): Promise<TableInfo | null> {
    const managerGlid = ManagerBySize[tableSize] ?? ServiceGLIDs.MANAGER;
    const sizeLabel = `${tableSize}-max`;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.emit('error', { message: `No ${sizeLabel} tables available` });
        resolve(null);
      }, 5000);

      const handleMessage = ({ from, body }: { from: string; body: string }) => {
        try {
          const message = JSON.parse(body) as Record<string, unknown>;

          // gethottable returns 'hot_table' response
          if (message.a === 'hot_table') {
            clearTimeout(timeout);
            networkService.off('message', handleMessage);

            if (message.status === 'no_place' || !message.to) {
              log.lobby.warning`Find table to watch: no tables available from ${managerGlid}`;
              this.emit('error', { message: `No ${sizeLabel} tables available` });
              resolve(null);
              return;
            }

            const croupierGlid = message.to as string;
            const table: TableInfo = {
              id: croupierGlid,
              name: this.formatTableName(croupierGlid),
              seats: tableSize,
              stakes: { smallBlind: 1, bigBlind: 2 }, // Default, will be updated
              playerCount: 0,
              avgPot: 0,
              handsPerHour: 0,
            };

            resolve(table);
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      networkService.on('message', handleMessage);
      // Use gethottable - just finds table, no seat reservation
      networkService.send(managerGlid, Commands.getHotTable());
    });
  }

  /**
   * Watch a table as spectator (by table size)
   * Player observes the game and can buy-in later
   * @param tableSize - Table size (2, 6, or 9)
   */
  async watchTableBySize(tableSize: TableSize): Promise<void> {
    // Use findTableToWatch (gethottable) - no seat reservation
    const table = await this.findTableToWatch(tableSize);
    if (!table) {
      return;
    }

    const { tableController } = await import('@/controllers/TableController');
    // Watch as spectator - no auto buy-in
    await tableController.watchTable(table, { spectatorMode: true });
  }

  /**
   * Quick play with specific buy-in amount
   * Uses # wannajumpin with amount to reserve seat and buy-in in one step
   * @param tableSize - Table size (2, 6, or 9)
   * @param amount - Buy-in amount
   * @param autoRefill - Auto refill flag
   * @param autoRebuy - Auto rebuy flag
   */
  async quickPlayWithBuyIn(
    tableSize: TableSize,
    amount: number,
    autoRefill: boolean,
    autoRebuy: boolean
  ): Promise<TableInfo | null> {
    const managerGlid = ManagerBySize[tableSize] ?? ServiceGLIDs.MANAGER;
    const sizeLabel = `${tableSize}-max`;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.emit('error', { message: `No ${sizeLabel} tables available` });
        resolve(null);
      }, 5000);

      const handleMessage = ({ from, body }: { from: string; body: string }) => {
        try {
          const message = JSON.parse(body) as Record<string, unknown>;

          if (message.a === 'handoff' && message.manager === managerGlid) {
            clearTimeout(timeout);
            networkService.off('message', handleMessage);

            if (message.status === 'no_place' || !message.to) {
              log.lobby.warning`Quick play with buyin: no tables available from ${managerGlid}`;
              this.emit('error', { message: `No ${sizeLabel} tables available` });
              resolve(null);
              return;
            }

            const croupierGlid = message.to as string;
            const table: TableInfo = {
              id: croupierGlid,
              name: this.formatTableName(croupierGlid),
              seats: tableSize,
              stakes: { smallBlind: 1, bigBlind: 2 },
              playerCount: 0,
              avgPot: 0,
              handsPerHour: 0,
            };

            // Navigate to table (already bought in via wannajumpin)
            import('@/controllers/TableController').then(({ tableController }) => {
              tableController.watchTable(table).then(() => { resolve(table); });
            });
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      networkService.on('message', handleMessage);

      // Send wannajumpin WITH amount - reserves seat AND buys in
      const autoRefillFlag = autoRefill ? 1 : 0;
      const autoRebuyFlag = autoRebuy ? 1 : 0;
      const command = Commands.wannaJumpIn(amount, autoRefillFlag, autoRebuyFlag);
      log.lobby.debug`Sending wannajumpin with buyin: ${amount} autoRefill=${autoRefill} autoRebuy=${autoRebuy}`;
      networkService.send(managerGlid, command);
    });
  }

  /**
   * Quick play - find best available table
   * Uses # wannajumpin command to let server find the best table
   * @param tableSize - Optional table size (2, 6, or 9). Determines which manager to use.
   */
  async quickPlay(tableSize?: TableSize): Promise<TableInfo | null> {
    // Determine which manager to use based on table size
    const managerGlid = tableSize && ManagerBySize[tableSize]
      ? ManagerBySize[tableSize]
      : ServiceGLIDs.MANAGER;

    const sizeLabel = tableSize ? `${tableSize}-max` : 'any';

    return new Promise((resolve) => {
      // Set up a one-time handler for the response
      const timeout = setTimeout(() => {
        this.emit('error', { message: `No ${sizeLabel} tables available` });
        resolve(null);
      }, 5000);

      const handleMessage = ({ from, body }: { from: string; body: string }) => {
        try {
          const message = JSON.parse(body) as Record<string, unknown>;

          // wannajumpin returns 'handoff' response
          // Note: The 'from' field is the croupier, but 'message.manager' contains the manager GLID
          if (message.a === 'handoff' && message.manager === managerGlid) {
            clearTimeout(timeout);
            networkService.off('message', handleMessage);

            // Check for error case (status: 'no_place' or empty 'to' field)
            if (message.status === 'no_place' || !message.to) {
              log.lobby.warning`Quick play: no tables available from ${managerGlid}`;
              this.emit('error', { message: `No ${sizeLabel} tables available` });
              resolve(null);
              return;
            }

            // Success - server found a table and reserved a seat
            const croupierGlid = message.to as string;

            // Create table info and watch it
            const table: TableInfo = {
              id: croupierGlid,
              name: this.formatTableName(croupierGlid),
              seats: tableSize ?? 6,
              stakes: { smallBlind: 1, bigBlind: 2 },
              playerCount: 0,
              avgPot: 0,
              handsPerHour: 0,
            };

            // Watch the table directly via TableController (bypasses selectTable lookup)
            import('@/controllers/TableController').then(({ tableController }) => {
              tableController.watchTable(table).then(() => { resolve(table); });
            });
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      networkService.on('message', handleMessage);

      // Send wannajumpin command to the appropriate manager
      networkService.send(managerGlid, Commands.wannaJumpIn());
    });
  }

  // ===========================================================================
  // GAME-TYPE-AWARE METHODS (T37)
  // ===========================================================================

  /**
   * Play Now: Auto-select a manager based on player balance and join.
   * Uses the formula: balance / X = maxAffordableBigBlind
   *
   * @param gameType - Cash, FastFold, or SNG
   * @param mode - How to join: spectator, auto-buyin, or auto-seat (manual buyin at table)
   */
  async playNow(
    gameType: GameType,
    mode: 'spectator' | 'auto-buyin' | 'auto-seat',
  ): Promise<TableInfo | null> {
    // Get player balance for auto-match
    const cachedState = accountService.getLastState();
    const playerBalance = cachedState?.chips ?? 0;

    const match = autoSelectManager(gameType, playerBalance);
    if (!match) {
      this.emit('error', { message: `No ${gameType} managers available` });
      return null;
    }

    log.lobby.debug`Play Now: ${gameType} ${mode} → ${match.managerGlid} (balance=${playerBalance})`;
    return this.joinManagerByMode(gameType, match.manager, match.managerGlid, mode);
  }

  /**
   * Join a specific manager's table.
   * Called from Custom Play wizard after player selects exact stakes/seats.
   *
   * @param manager - The specific manager config chosen by the player
   * @param mode - How to join: spectator, auto-buyin, or auto-seat
   */
  async joinManager(
    manager: ManagerConfig,
    mode: 'spectator' | 'auto-buyin' | 'auto-seat',
  ): Promise<TableInfo | null> {
    log.lobby.debug`Join manager: ${manager.name} ${mode}`;
    return this.joinManagerByMode(manager.gameType, manager, manager.name, mode);
  }

  /**
   * Watch a game type as spectator.
   * Finds a hot table of the given game type.
   *
   * @param gameType - Cash, FastFold, or SNG
   */
  async watchByGameType(gameType: GameType): Promise<void> {
    // Pick a mid-level manager for this game type
    const cachedState = accountService.getLastState();
    const playerBalance = cachedState?.chips ?? 10000;

    const match = autoSelectManager(gameType, playerBalance);
    if (!match) {
      this.emit('error', { message: `No ${gameType} tables available` });
      return;
    }

    return this.joinManagerByMode(gameType, match.manager, match.managerGlid, 'spectator').then(() => {});
  }

  /**
   * Internal: join a manager's table in the specified mode.
   */
  private async joinManagerByMode(
    gameType: GameType,
    manager: ManagerConfig,
    managerGlid: string,
    mode: 'spectator' | 'auto-buyin' | 'auto-seat',
  ): Promise<TableInfo | null> {
    if (mode === 'spectator') {
      return this.findTableToWatchByManager(managerGlid, manager);
    }

    // For auto-buyin: Cash/FF buy in at maxBuyin (full stack), SNG uses tier buyin
    if (mode === 'auto-buyin') {
      const buyinAmount = 'maxBuyin' in manager
        ? manager.maxBuyin
        : 'buyin' in manager ? manager.buyin : 0;
      return this.findAndJoinByManager(managerGlid, manager, buyinAmount);
    }

    // For auto-seat (manual buy-in at table), join without amount
    return this.findAndJoinByManager(managerGlid, manager, 0);
  }

  /**
   * Find a table via a specific manager GLID using # wannajumpin
   */
  private async findAndJoinByManager(
    managerGlid: string,
    manager: ManagerConfig,
    buyinAmount: number,
  ): Promise<TableInfo | null> {
    const seats = 'seats' in manager ? manager.seats : 6;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.emit('error', { message: `No tables available for ${manager.name}` });
        resolve(null);
      }, 5000);

      const handleMessage = ({ from, body }: { from: string; body: string }) => {
        try {
          const message = JSON.parse(body) as Record<string, unknown>;

          if (message.a === 'handoff' && message.manager === managerGlid) {
            clearTimeout(timeout);
            networkService.off('message', handleMessage);

            if (message.status === 'no_place' || !message.to) {
              this.emit('error', { message: `No tables available for ${manager.name}` });
              resolve(null);
              return;
            }

            const croupierGlid = message.to as string;
            const table: TableInfo = {
              id: croupierGlid,
              name: this.formatTableName(croupierGlid),
              seats: seats as TableSize,
              stakes: {
                smallBlind: 'smallBlind' in manager ? manager.smallBlind : 1,
                bigBlind: 'bigBlind' in manager ? manager.bigBlind : 2,
              },
              playerCount: 0,
              avgPot: 0,
              handsPerHour: 0,
            };

            import('@/controllers/TableController').then(({ tableController }) => {
              tableController.watchTable(table).then(() => { resolve(table); });
            });
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      networkService.on('message', handleMessage);

      if (buyinAmount > 0) {
        networkService.send(managerGlid, Commands.wannaJumpIn(buyinAmount));
      } else {
        networkService.send(managerGlid, Commands.wannaJumpIn());
      }
    });
  }

  /**
   * Find a table to watch via a specific manager GLID using # gethottable
   */
  private async findTableToWatchByManager(
    managerGlid: string,
    manager: ManagerConfig,
  ): Promise<TableInfo | null> {
    const seats = 'seats' in manager ? manager.seats : 6;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.emit('error', { message: `No tables available for ${manager.name}` });
        resolve(null);
      }, 5000);

      const handleMessage = ({ from, body }: { from: string; body: string }) => {
        try {
          const message = JSON.parse(body) as Record<string, unknown>;

          if (message.a === 'hot_table') {
            clearTimeout(timeout);
            networkService.off('message', handleMessage);

            if (message.status === 'no_place' || !message.to) {
              this.emit('error', { message: `No tables available for ${manager.name}` });
              resolve(null);
              return;
            }

            const croupierGlid = message.to as string;
            const table: TableInfo = {
              id: croupierGlid,
              name: this.formatTableName(croupierGlid),
              seats: seats as TableSize,
              stakes: {
                smallBlind: 'smallBlind' in manager ? manager.smallBlind : 1,
                bigBlind: 'bigBlind' in manager ? manager.bigBlind : 2,
              },
              playerCount: 0,
              avgPot: 0,
              handsPerHour: 0,
            };

            // Watch as spectator
            import('@/controllers/TableController').then(({ tableController }) => {
              tableController.watchTable(table, { spectatorMode: true }).then(() => { resolve(table); });
            });
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      networkService.on('message', handleMessage);
      networkService.send(managerGlid, Commands.getHotTable());
    });
  }

  /**
   * Refresh games list
   */
  async refresh(): Promise<void> {
    await this.loadGames();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupNetworkHandlers(): void {
    // Handle server responses
    networkService.on('message', ({ from, body }) => {
      try {
        const message = JSON.parse(body) as Record<string, unknown>;
        log.lobby.debug`Received message: ${message.a} from: ${from}`;

        // Handle hot_table response from manager
        if (message.a === 'hot_table') {
          this.handleHotTableResponse(message as unknown as HotTableMessage);
        }

        // Handle state response from croupier
        if (message.a === 'state' || message.a === 'state2') {
          this.handleStateResponse(from, message as unknown as StateMessage);
        }

        // Handle legacy games response (if server implements it)
        if (message.a === 'games' && message.managers) {
          const managers = this.parseManagers(message.managers as unknown[]);
          setManagers(managers);
          this.emit('gamesLoaded', { managers });
        }

        if (message.a === 'tables' && message.tables) {
          const tables = this.parseTables(message.tables as unknown[]);
          setTables(tables);
          this.emit('tablesLoaded', { tables });
        }
      } catch {
        // Not a JSON message we handle
      }
    });
  }

  /**
   * Handle hot_table response from manager
   */
  private handleHotTableResponse(message: HotTableMessage): void {
    log.lobby.debug`Hot table response: ${JSON.stringify(message)}`;

    if (message.status === 'ok' && message.to) {
      const croupierGlid = message.to;

      // Don't add duplicates
      if (this.discoveredCroupiers.has(croupierGlid)) {
        return;
      }

      this.discoveredCroupiers.add(croupierGlid);

      // Create a table entry from the croupier
      const table: TableInfo = {
        id: croupierGlid,
        name: this.formatTableName(croupierGlid),
        seats: 6, // Default from server config
        stakes: { smallBlind: 1, bigBlind: 2 }, // Default from server config
        playerCount: 0,
        avgPot: 0,
        handsPerHour: 0,
      };

      // Add to current tables
      const currentTables = lobbyState.tables.get();
      const newTables = [...currentTables, table];
      setTables(newTables);

      // Update manager table count
      const managers = lobbyState.managers.get();
      if (managers.length > 0 && managers[0]) {
        const updatedManager = {
          ...managers[0],
          tableCount: newTables.length,
          tables: newTables,
        };
        setManagers([updatedManager]);
      }

      this.emit('tablesLoaded', { tables: newTables });

      // Request to watch this croupier to get table details
      // Note: # watch subscribes to table updates, server responds with state
      log.lobby.debug`Requesting watch from croupier: ${croupierGlid}`;
      networkService.send(croupierGlid, Commands.watch());
    } else if (message.status === 'no_place') {
      log.lobby.info`No tables available`;
      this.emit('error', { message: 'No tables available' });
    }
  }

  /**
   * Handle state response from croupier - updates table with real data
   */
  private handleStateResponse(croupierGlid: string, message: StateMessage): void {
    log.lobby.debug`State response from: ${croupierGlid} ${JSON.stringify(message)}`;

    const gameInfo = message.gameInfo;
    const players = message.players || [];

    // Update the table with real data from server
    const currentTables = lobbyState.tables.get();
    const updatedTables = currentTables.map((table) => {
      if (table.id === croupierGlid) {
        return {
          ...table,
          seats: (gameInfo?.numSits || table.seats),
          stakes: {
            smallBlind: gameInfo?.sb || table.stakes.smallBlind,
            bigBlind: gameInfo?.bb || table.stakes.bigBlind,
          },
          playerCount: players.length,
        };
      }
      return table;
    });

    setTables(updatedTables);

    // Update manager tables too
    const managers = lobbyState.managers.get();
    if (managers.length > 0 && managers[0]) {
      const updatedManager = {
        ...managers[0],
        tables: updatedTables,
        playerCount: updatedTables.reduce((sum, t) => sum + t.playerCount, 0),
      };
      setManagers([updatedManager]);
    }

    this.emit('tablesLoaded', { tables: updatedTables });
  }

  /**
   * Format a croupier GLID into a friendly table name
   */
  private formatTableName(croupierGlid: string): string {
    // Extract number from glid like "croupier_1" or "table1"
    const match = croupierGlid.match(/(\d+)/);
    if (match && match[1]) {
      return `Table ${match[1]}`;
    }
    return croupierGlid;
  }

  private parseManagers(data: unknown[]): GameManager[] {
    // Parse server response into GameManager objects
    return data.map((item: unknown) => {
      const m = item as Record<string, unknown>;
      return {
        id: String(m.id ?? ''),
        name: String(m.name ?? ''),
        type: (m.type as 'cash' | 'sng' | 'mtt') ?? 'cash',
        description: String(m.description ?? ''),
        icon: String(m.icon ?? ''),
        playerCount: Number(m.playerCount ?? 0),
        tableCount: Number(m.tableCount ?? 0),
        tables: this.parseTables((m.tables as unknown[]) ?? []),
      };
    });
  }

  private parseTables(data: unknown[]): TableInfo[] {
    return data.map((item: unknown) => {
      const t = item as Record<string, unknown>;
      const stakes = t.stakes as Record<string, number> | undefined;
      return {
        id: String(t.id ?? ''),
        name: String(t.name ?? ''),
        seats: (t.seats as TableSize) ?? 9,
        stakes: {
          smallBlind: stakes?.sb ?? stakes?.smallBlind ?? 1,
          bigBlind: stakes?.bb ?? stakes?.bigBlind ?? 2,
        },
        playerCount: Number(t.players ?? t.playerCount ?? 0),
        avgPot: Number(t.avgPot ?? 0),
        handsPerHour: Number(t.handsPerHour ?? 0),
      };
    });
  }

  private getStakesLevel(table: TableInfo): StakesLevel {
    const bb = table.stakes.bigBlind;
    if (bb <= 2) return 'micro' as StakesLevel;
    if (bb <= 10) return 'low' as StakesLevel;
    if (bb <= 50) return 'mid' as StakesLevel;
    return 'high' as StakesLevel;
  }
}

/** Singleton lobby controller instance */
export const lobbyController = LobbyController.getInstance();
